import React, { useEffect, useState, useCallback, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import Select from 'react-select';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';

import {
  hasRoles,
  EDIT_PARTICIPANT_REVIEW_ROLES,
  QUERY_RESPONSE_ROLES,
  EDIT_QUERY_RESPONSE_ONLY_ROLES,
} from '../../../../util/RoleUtil';
import { DATE_PICKER_POPPER_MODIFIERS } from '../../../../util/DateUtil';
import { formatDateAsString } from '../../../../util/StringUtil';

import { retrieveAllSystemUserPersons, viewStaffForStudy } from '../../../../services/PersonService';
import { retrieveQueryTypes, viewQueryType } from '../../../../services/QueryTypeService';
import { createQuery, updateQuery, viewAvailableFormsForQuery } from '../../../../services/QueryService';
import { viewNotificationInterval } from '../../../../services/NotificationIntervalService';

import { SaveButton } from '../../../common/button/save-button/SaveButton';
import { ToolTip } from '../../../common/tool-tip/ToolTip';
import { Checkbox } from '../../../common/checkbox/Checkbox';

import './ParticipantReviewCreate.css';

const PRIORITY_LIST = ['Low', 'Medium', 'High', 'Critical'];
const STATUS_LIST = ['Answered', 'Closed', 'Invalid Query', 'Open', 'Re-Issued', 'Waiting'];

export const ParticipantQueryCreateTab = ({
  query,
  handleSaveClick,
  setShowQueriesTab,
  setShowQueriesTable,
  navigateTo,
  context,
  setActiveTab,
  setSpinnerText,
  showToaster,
  toolTipsEnabled,
  editMode,
  isNew,
  visitNotLinked,
  retrieveReview,
  review,
  userHasViewPermission,
}) => {
  const [queryDate, setQueryDate] = useState(null);
  const [studyForm, setStudyForm] = useState(null);
  const [additionalComments, setAdditionalComments] = useState('');
  const [qcReviewPageSource, setQcReviewPageSource] = useState(true);
  const [qcReviewPageDatabase, setQcReviewPageDatabase] = useState(false);
  const [formSection, setFormSection] = useState(null);
  const [queryType, setQueryType] = useState(null);
  const [queryResponse, setQueryResponse] = useState(null);
  const [priority, setPriority] = useState(null);
  const [responsiblePerson, setResponsiblePerson] = useState(null);
  const [selectedCcUsers, setSelectedCcUsers] = useState([]);
  const [selectedEscalationUsers, setSelectedEscalationUsers] = useState([]);
  const [status, setStatus] = useState(null);
  const [actionTaken, setActionTaken] = useState('');
  const [actionedBy, setActionedBy] = useState(null);
  const [actionDate, setActionDate] = useState(null);
  const [studyFormList, setStudyFormList] = useState([]);
  const [formSectionList, setFormSectionList] = useState([]);
  const [queryTypeList, setQueryTypes] = useState([]);
  const [queryResponseList, setQueryResponseList] = useState([]);
  const [responsiblePersonList, setResponsiblePersonList] = useState([]);
  const [allPersonList, setAllPersonList] = useState([]);
  const [queryDurationDays, setQueryDurationDays] = useState('');
  const [queryIntervalDays, setQueryIntervalDays] = useState('');
  const [queryDateError, setQueryDateError] = useState('');
  const [priorityError, setPriorityError] = useState('');
  const [responsiblePersonError, setResponsiblePersonError] = useState('');
  const [statusError, setStatusError] = useState('');
  const [actionDateError, setActionDateError] = useState('');
  const [actionByError, setActionByError] = useState('');
  const [queryResponseError, setQueryResponseError] = useState('');

  const getUserDropdowns = useCallback(async () => {
    setSpinnerText('Retrieving Users');
    const [data, error] = await retrieveAllSystemUserPersons(context.username);
    if (data) {
      if (data.responseStatus === 'Success') {
        const formattedUsers = data.viewAllPersonSystemUserVOs.map((user) => ({
          value: user,
          label: user.name,
        }));
        setAllPersonList(formattedUsers);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  }, [context.username, setSpinnerText, showToaster]);

  const retrieveDropdownUsersForStudy = useCallback(async () => {
    const viewRoleInStudyPersonRequest = {
      locationExternalReference: review.locationExternalReference,
      participantExternalReference: review.participantExternalReference,
      studyExternalReference: review.studyExternalReference,
    };

    setSpinnerText('Retrieving Users');
    const [data, error] = await viewStaffForStudy(context.username, viewRoleInStudyPersonRequest);
    if (data) {
      if (data.responseStatus === 'Success') {
        const formattedUsers = data.viewAllPersonSystemUserVOs.map((user) => ({
          label: user.name,
          value: user.externalReference,
        }));
        setResponsiblePersonList(formattedUsers);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.username, setSpinnerText, showToaster, editMode, context.loggedInUser.externalReference, review]);

  const getStudyForms = useCallback(async () => {
    if (!context.username || !review?.reviewExternalReference) {
      return;
    }

    setSpinnerText('Retrieving Forms');
    const [data, error] = await viewAvailableFormsForQuery(context.username, review.reviewExternalReference);
    if (data) {
      if (data.responseStatus === 'Success') {
        const forms = data.availableFormVOS.map((form) => ({
          value: form,
          label: form.name,
        }));
        setStudyFormList(forms);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  }, [context.username, review, setSpinnerText, showToaster]);

  const getQueryTypes = useCallback(async () => {
    setSpinnerText('Retrieving Query Types');
    const [data, error] = await retrieveQueryTypes(context.username);
    if (data) {
      if (data.responseStatus === 'Success') {
        const types = data.queryTypes.map((type) => ({
          label: type.name,
          value: type.externalReference,
        }));
        setQueryTypes(types);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  }, [context.username, setSpinnerText, showToaster]);

  const getNotificationInterval = useCallback(async () => {
    setSpinnerText('Retrieving Notification Settings');
    const [data, error] = await viewNotificationInterval(context.username);
    if (data) {
      if (data.responseStatus === 'Success' && data.notificationInterval) {
        setQueryDurationDays(data.notificationInterval.queryReviewEscalationDays || '');
        setQueryIntervalDays(data.notificationInterval.queryReviewHours || '');
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  }, [setSpinnerText, showToaster, context.username]);

  const getQueryTypeResponses = useCallback(
    async (queryTypeExtRef) => {
      setSpinnerText('Retrieving Query Responses');
      const [data, error] = await viewQueryType(context.username, queryTypeExtRef);
      if (data) {
        if (data.responseStatus === 'Success') {
          const responses = data.queryType.availableResponses.map((response) => ({
            value: response,
            label: response,
          }));
          setQueryResponseList(responses);
        } else {
          showToaster(data);
        }
      } else {
        showToaster({
          responseStatus: 'Error',
          responseMessage: error.message,
        });
      }
      setSpinnerText(null);
    },
    [context.username, setSpinnerText, showToaster]
  );

  useEffect(() => {
    if (!queryType) return;

    getQueryTypeResponses(queryType.value);
  }, [queryType, getQueryTypeResponses]);

  useEffect(() => {
    getUserDropdowns();

    if (review?.studyExternalReference || review?.participantExternalReference) {
      retrieveDropdownUsersForStudy();
    }

    getQueryTypes();

    if (review?.reviewExternalReference) {
      getStudyForms();
    }
  }, [
    getQueryTypes,
    getStudyForms,
    getUserDropdowns,
    retrieveDropdownUsersForStudy,
    review?.studyExternalReference,
    review?.participantExternalReference,
    review?.reviewExternalReference,
  ]);

  useEffect(() => {
    if (query && !query.emailFrequencyHours && !query.escalateAfterDays) {
      getNotificationInterval();
    }
  }, [query, getNotificationInterval]);

  useEffect(() => {
    setActiveTab('participantReviewCreateQuery');

    if (query && Object.keys(query).length !== 0) {
      setQueryDate(query.dateOfQuery ? new Date(query.dateOfQuery) : null);

      if (query.qcReviewPage === 'Source Review') {
        setQcReviewPageSource(true);
        setQcReviewPageDatabase(false);
      } else {
        setQcReviewPageDatabase(true);
        setQcReviewPageSource(false);
      }

      setStudyForm(
        query.studyForm
          ? typeof query.studyForm === 'object'
            ? { value: query.studyFormExternalReference.value, label: query.studyForm.label }
            : { value: query.studyFormExternalReference, label: query.studyForm }
          : null
      );

      setFormSection(
        query.formSection
          ? typeof query.formSection === 'object'
            ? { value: query.formSection.value, label: query.formSection.label }
            : { value: query.formSection, label: query.formSection }
          : null
      );

      setQueryType({ label: query.queryType, value: query.queryTypeExternalReference });

      setQueryResponse(
        query.queryResponse
          ? typeof query.queryResponse === 'object'
            ? { value: query.queryResponse.value, label: query.queryResponse.label }
            : { value: query.queryResponse, label: query.queryResponse }
          : null
      );

      setPriority(
        query.priority
          ? typeof query.priority === 'object'
            ? { value: query.priority.value, label: query.priority.label }
            : { value: query.priority, label: query.priority }
          : null
      );

      setStatus(
        query.status
          ? typeof query.status === 'object'
            ? { value: query.status.value, label: query.status.label }
            : { value: query.status, label: query.status }
          : null
      );

      setAdditionalComments(query.queryDescription || '');
      setQueryIntervalDays(query.emailFrequencyHours);
      setQueryDurationDays(query.escalateAfterDays);
      setResponsiblePerson({ label: query.responsiblePerson, value: query.responsiblePersonExternalReference });

      const formattedCcUsers = query.ccUsers.map((user) => ({
        value: user,
        label: user?.name,
      }));
      setSelectedCcUsers(formattedCcUsers);

      const formattedEscalatedUsers = query.escalateToUsers.map((user) => ({
        value: user,
        label: user?.name,
      }));
      setSelectedEscalationUsers(formattedEscalatedUsers);

      setActionTaken(query.actionTaken || '');
      setActionedBy({ label: query.actionedBy, value: query.actionedByExternalReference });
      setActionDate(query.actionDate ? new Date(query.actionDate) : null);
    }
  }, [query, setActiveTab]);

  const userCanEditParticipantReview = hasRoles(context, EDIT_PARTICIPANT_REVIEW_ROLES);
  const userCanEditQueryResponseOnly = editMode && !isNew && hasRoles(context, EDIT_QUERY_RESPONSE_ONLY_ROLES);

  const fieldsDisabled = !userCanEditParticipantReview || userCanEditQueryResponseOnly;

  const queryResponseAndCommentsDisabled = !userCanEditParticipantReview && !userCanEditQueryResponseOnly;

  const responsiblePersonDisabled = !userCanEditParticipantReview && !userCanEditQueryResponseOnly;

  const responseFieldsDisabled = useMemo(() => {
    if (query?.createdBy === 'DATA_MIGRATION') return false;

    if (!hasRoles(context, QUERY_RESPONSE_ROLES)) return true;

    const currentUserPersonExternalReference = context.loggedInUser.personExternalReference;

    const isResponsiblePerson = responsiblePerson?.value === currentUserPersonExternalReference;

    if (isResponsiblePerson) return false;

    const isCreator = query.createdByExternalReference === currentUserPersonExternalReference;

    if (isCreator) return false;

    const isCcUser = selectedCcUsers.some(
      (user) => user?.value?.personExternalReference === currentUserPersonExternalReference
    );

    if (isCcUser) return false;

    const isEscalationUser = selectedEscalationUsers.some(
      (user) => user?.value?.personExternalReference === currentUserPersonExternalReference
    );

    return !isEscalationUser;
  }, [context, responsiblePerson?.value, selectedCcUsers, selectedEscalationUsers, query]);

  const statusActionFieldsDisabled = userCanEditParticipantReview
    ? false
    : responseFieldsDisabled || userCanEditQueryResponseOnly;

  const handleStudyFormSelection = (selectedForm) => {
    setStudyForm(selectedForm);
    setFormSectionList(selectedForm?.value?.sections);
  };

  const handleCcUsersChange = (selectedOptions) => {
    setSelectedCcUsers((prevSelectedUsers) => {
      const prevUsersArray = Array.isArray(prevSelectedUsers) ? prevSelectedUsers : [];
      const uniqueUsers = selectedOptions.filter(
        (user) => !prevUsersArray.some((prevUser) => prevUser.value === user.value)
      );
      return [...prevUsersArray, ...uniqueUsers];
    });
  };

  const handleRemoveCcUser = (index) => {
    setSelectedCcUsers((prevSelectedUsers) => {
      const updatedUsers = [...prevSelectedUsers];
      updatedUsers.splice(index, 1);
      return updatedUsers;
    });
  };

  const handleEscalatedUsersChange = (selectedOptions) => {
    setSelectedEscalationUsers((prevSelectedUsers) => {
      const prevUsersArray = Array.isArray(prevSelectedUsers) ? prevSelectedUsers : [];
      const uniqueUsers = selectedOptions.filter(
        (user) => !prevUsersArray.some((prevUser) => prevUser.value === user.value)
      );
      return [...prevUsersArray, ...uniqueUsers];
    });
  };

  const handleRemoveEscalatedUser = (index) => {
    setSelectedEscalationUsers((prevSelectedUsers) => {
      const updatedUsers = [...prevSelectedUsers];
      updatedUsers.splice(index, 1);
      return updatedUsers;
    });
  };

  const handleQueryTypeSelect = (selectedType) => {
    setQueryType(selectedType);
    setQueryResponse(null);
  };

  const getDateWithoutTime = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const validateFields = () => {
    let isValid = true;

    if (!queryResponse && (status?.label === 'Closed' || status?.label === 'Closed Migrated')) {
      setQueryResponseError('*REQUIRED');
      isValid = false;
    } else {
      setQueryResponseError('');
    }

    if (userCanEditQueryResponseOnly) {
      return isValid;
    }

    const currentDate = new Date();

    if (!queryDate) {
      setQueryDateError('*REQUIRED');
      isValid = false;
    } else if (queryDate > currentDate) {
      setQueryDateError('Query Date cannot be future dated.');
      isValid = false;
    } else {
      setQueryDateError('');
    }

    if (!priority || priority?.label === null) {
      setPriorityError('*REQUIRED');
      isValid = false;
    } else {
      setPriorityError('');
    }

    if (!responsiblePerson?.value) {
      setResponsiblePersonError('*REQUIRED');
      isValid = false;
    } else {
      setResponsiblePersonError('');
    }

    if (status && (status.label === 'Open' || status.label === 'Re-Issued')) {
      if (!actionedBy) {
        setActionByError('*REQUIRED');
        isValid = false;
      } else {
        setActionByError('');
      }

      const actionDay = getDateWithoutTime(new Date(actionDate));
      const queryDay = getDateWithoutTime(new Date(queryDate));

      if (!actionDate) {
        setActionDateError('*REQUIRED');
        isValid = false;
      } else if (actionDate > currentDate) {
        setActionDateError('Action Date cannot be future dated.');
        isValid = false;
      } else if (queryDate && actionDay < queryDay) {
        setActionDateError('Action Date cannot be before Query Date.');
        isValid = false;
      } else {
        setActionDateError('');
      }
    }

    if ((actionDate || actionedBy) && !status) {
      setStatusError('*REQUIRED');
      isValid = false;
    } else {
      setStatusError('');
    }

    return isValid;
  };

  const handleSaveAndCloseEditClick = async () => {
    const validated = validateFields();

    if (!validated) {
      return;
    }

    let qcReviewPage;
    if (qcReviewPageSource) {
      qcReviewPage = 'Source Review';
    } else {
      qcReviewPage = 'Database';
    }

    let stringActionDate = formatDateAsString(actionDate);
    if (stringActionDate === '1970-01-01') {
      stringActionDate = '';
    }

    if (isNew) {
      const newQuery = {
        reviewExternalReference: review.reviewExternalReference,
        dateOfQuery: formatDateAsString(queryDate) || '',
        studyForm: studyForm?.value?.formExternalReference || '',
        qcReviewPage: qcReviewPage,
        formSection: formSection?.value || '',
        queryType: queryType?.value || '',
        queryResponse: queryResponse?.value || '',
        priority: priority?.value || '',
        queryDescription: additionalComments || '',
        responsiblePerson: responsiblePerson?.value || '',
        ccUsers:
          selectedCcUsers?.map((user) => user?.value?.personExternalReference || user?.value?.externalReference) || [],
        escalateToUsers:
          selectedEscalationUsers?.map(
            (user) => user?.value?.personExternalReference || user?.value?.externalReference
          ) || [],
        emailFrequencyHours: queryIntervalDays,
        escalateAfterDays: queryDurationDays,
      };
      saveQuery(newQuery);
    } else {
      const updatedQuery = {
        ...query,
        reviewExternalReference: review.reviewExternalReference,

        dateOfQuery: formatDateAsString(queryDate) || query?.dateOfQuery || '',

        studyForm:
          studyForm?.value?.formExternalReference ||
          studyForm?.value ||
          query?.studyFormExternalReference ||
          query?.studyForm ||
          '',

        qcReviewPage,

        formSection: formSection?.value || query?.formSection || '',

        queryType: queryType?.value || query?.queryTypeExternalReference || query?.queryType || '',

        queryResponse: queryResponse?.value || '',

        priority: priority?.value || query?.priority || '',

        queryDescription: additionalComments || '',

        responsiblePerson: responsiblePerson?.value || query?.responsiblePersonExternalReference || '',

        ccUsers:
          selectedCcUsers?.map(
            (user) => user?.value?.personExternalReference || user?.value?.externalReference || user?.value
          ) || [],

        escalateToUsers:
          selectedEscalationUsers?.map(
            (user) => user?.value?.personExternalReference || user?.value?.externalReference || user?.value
          ) || [],

        emailFrequencyHours: queryIntervalDays,
        escalateAfterDays: queryDurationDays,

        status: status?.value || query?.status || '',
        actionTaken: actionTaken || '',
        actionedBy: actionedBy?.value || null,
        actionDate: stringActionDate || null,
      };
      updateQueryHandle(updatedQuery);
    }

    if (userHasViewPermission) {
      handleSaveClick('participantQueryTable');
      setShowQueriesTab(false);
      setShowQueriesTable(true);
    }
  };

  const handleCancelEditClick = () => {
    if (!userHasViewPermission) {
      navigateTo('dashboard');
      return;
    }

    handleSaveClick('participantQueryTable');
    setShowQueriesTab(false);
    setShowQueriesTable(true);
  };

  const handleSaveAndCloseClick = () => {
    const validated = validateFields();

    if (!validated) {
      return;
    }

    let qcReviewPage;
    if (qcReviewPageSource) {
      qcReviewPage = 'Source Review';
    } else {
      qcReviewPage = 'Database';
    }

    const queryToSave = {
      reviewExternalReference: review.reviewExternalReference || '',
      dateOfQuery: formatDateAsString(queryDate) || '',
      studyForm: studyForm?.value?.formExternalReference || '',
      qcReviewPage: qcReviewPage,
      formSection: formSection?.value || '',
      queryType: queryType?.value || '',
      queryResponse: queryResponse?.value || '',
      priority: priority?.value || '',
      queryDescription: additionalComments || '',
      responsiblePerson: responsiblePerson?.value || '',
      ccUsers: selectedCcUsers?.map((user) => user?.value?.externalReference) || [],
      escalateToUsers: selectedEscalationUsers?.map((user) => user?.value?.externalReference) || [],
      emailFrequencyHours: queryIntervalDays,
      escalateAfterDays: queryDurationDays,
    };
    saveQuery(queryToSave).then(() => navigateTo('participantReviewsTable'));
  };

  const saveQuery = async (createQueryRequest) => {
    setSpinnerText('Creating Query');
    const [data, error] = await createQuery(context.username, createQueryRequest);
    if (data) {
      if (data.responseStatus === 'Success') {
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Query successfully created',
        });

        await retrieveReview(context.username, review.reviewExternalReference);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  };

  const updateQueryHandle = async (queryToUpdate) => {
    setSpinnerText('Updating query');
    const [data, error] = await updateQuery(context.username, queryToUpdate);
    if (data) {
      if (data.responseStatus === 'Success') {
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'Query Updated',
        });

        await retrieveReview(context.username, review.reviewExternalReference);
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  };

  const handleSaveAndNewClick = () => {
    const validated = validateFields();

    if (!validated) {
      return;
    }

    let qcReviewPage;
    if (qcReviewPageSource) {
      qcReviewPage = 'Source Review';
    } else {
      qcReviewPage = 'Database';
    }

    const queryToSave = {
      reviewExternalReference: review.reviewExternalReference || '',
      dateOfQuery: formatDateAsString(queryDate) || '',
      studyForm: studyForm?.value?.formExternalReference || '',
      qcReviewPage: qcReviewPage,
      formSection: formSection?.value || '',
      queryType: queryType?.value || '',
      queryResponse: queryResponse?.value || '',
      priority: priority?.value || '',
      queryDescription: additionalComments || '',
      responsiblePerson: responsiblePerson?.value || '',
      ccUsers: selectedCcUsers?.map((user) => user?.value?.externalReference) || [],
      escalateToUsers: selectedEscalationUsers?.map((user) => user?.value?.externalReference) || [],
      emailFrequencyHours: queryIntervalDays,
      escalateAfterDays: queryDurationDays,
    };

    saveQuery(queryToSave).then(() => resetForm());
  };

  const resetForm = () => {
    setQueryDate(null);
    setStudyForm(null);
    setQcReviewPageSource(true);
    setQcReviewPageDatabase(false);
    setFormSection(null);
    setQueryType(null);
    setQueryResponse(null);
    setPriority(null);
    setAdditionalComments('');
    setResponsiblePerson(null);
    setSelectedCcUsers([]);
    setSelectedEscalationUsers([]);
    setQueryDurationDays(null);
    setQueryIntervalDays(null);
  };

  return (
    <div className="tab-container participant-query-create-tab">
      <div className="query-input-container">
        <div className="form-section-left">
          <div className="input-container">
            <label className={'input-label'}>Query Date:</label>
            <DatePicker
              selected={queryDate}
              className={`text-input ${queryDateError && 'error-border'}`}
              dateFormat="dd-MM-yyyy"
              showPopperArrow={false}
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              disabled={fieldsDisabled}
              placeholderText="Select..."
              onChange={(selectedDate) => setQueryDate(selectedDate)}
              popperModifiers={DATE_PICKER_POPPER_MODIFIERS}
            />
            <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon" />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'reviewQueryDate123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Query Date:'}
              />
            )}
          </div>
          <div className="error-message">
            {queryDateError && <div className="small-red-text">{queryDateError}</div>}
          </div>

          {visitNotLinked === true && (
            <div className="input-container">
              <label className={'input-label'}>Study Form:</label>
              <Select
                options={studyFormList}
                closeMenuOnSelect={true}
                onChange={handleStudyFormSelection}
                value={studyForm || null}
                className="input-select"
                isDisabled={fieldsDisabled}
                classNamePrefix="input-select"
              />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference={'reviewQueryStudyForm123'}
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName={'Study Form:'}
                />
              )}
            </div>
          )}

          <div className="input-container">
            <label className={'input-label'}>QC Review Page:</label>
            <Checkbox
              color={'#000000'}
              checked={qcReviewPageSource}
              onChange={() => {
                setQcReviewPageSource(true);
                setQcReviewPageDatabase(false);
              }}
              fillStyle={true}
              disabled={fieldsDisabled}
              label={'Source'}
            />
            <Checkbox
              color={'#000000'}
              checked={qcReviewPageDatabase}
              onChange={() => {
                setQcReviewPageSource(false);
                setQcReviewPageDatabase(true);
              }}
              fillStyle={true}
              disabled={fieldsDisabled}
              label={'Database'}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'qcReviewPage123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'QC Review Page:'}
              />
            )}
          </div>

          {visitNotLinked === true && (
            <div className="input-container">
              <label className={'input-label'}>Form Section:</label>
              <Select
                options={formSectionList.map((section) => ({
                  value: section,
                  label: section,
                }))}
                closeMenuOnSelect={true}
                onChange={setFormSection}
                value={formSection || null}
                className="input-select"
                classNamePrefix="input-select"
                isDisabled={fieldsDisabled}
              />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference={'queryFormSection123'}
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName={'Form Section:'}
                />
              )}
            </div>
          )}

          <div className="input-container">
            <label className={'input-label'}>Query Type:</label>
            <Select
              options={queryTypeList}
              closeMenuOnSelect={true}
              onChange={handleQueryTypeSelect}
              value={queryType || null}
              className="input-select"
              classNamePrefix="input-select"
              isDisabled={fieldsDisabled}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'reviewQueryType123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Query Type:'}
              />
            )}
          </div>

          <div className="input-container">
            <label className={'input-label'}>Query Response:</label>
            <Select
              options={queryResponseList}
              closeMenuOnSelect={true}
              onChange={setQueryResponse}
              value={queryResponse || null}
              className={`input-select${queryResponseError && 'error-border'}`}
              classNamePrefix={`input-select${queryResponseError && 'error-border'}`}
              isDisabled={queryResponseAndCommentsDisabled}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'reviewQueryResponse123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Query Response:'}
              />
            )}
          </div>
          <div className="error-message">
            {queryResponseError && <div className="small-red-text">{queryResponseError}</div>}
          </div>

          <div className="input-container">
            <label className={'input-label'}>Priority:</label>
            <Select
              options={PRIORITY_LIST.map((priority) => ({
                value: priority,
                label: priority,
              }))}
              closeMenuOnSelect={true}
              onChange={setPriority}
              value={priority || null}
              className={`input-select${priorityError && 'error-border'}`}
              classNamePrefix={`input-select${priorityError && 'error-border'}`}
              isDisabled={fieldsDisabled}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'reviewQueryPriority123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Priority:'}
              />
            )}
          </div>
          <div className="error-message">{priorityError && <div className="small-red-text">{priorityError}</div>}</div>

          <div className="input-container">
            <label className={'input-area-label'}>Additional Comments:</label>
            <textarea
              className="input-area-sm"
              spellCheck={true}
              type="text"
              value={additionalComments}
              disabled={queryResponseAndCommentsDisabled}
              onChange={({ target }) => {
                setAdditionalComments(target.value);
              }}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'queryAdditionalComments123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Additional Comments:'}
              />
            )}
          </div>
        </div>

        <div className="form-section-right">
          <div className="input-container">
            <label className={'input-label-right'}>Responsible Person:</label>
            <Select
              options={responsiblePersonList}
              closeMenuOnSelect={true}
              onChange={setResponsiblePerson}
              value={responsiblePerson || null}
              className={`input-select${responsiblePersonError && 'error-border'}`}
              classNamePrefix={`input-select${responsiblePersonError && 'error-border'}`}
              isDisabled={responsiblePersonDisabled}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'QueryResponsiblePerson123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Responsible Person:'}
              />
            )}
          </div>
          <div className="error-message-right">
            {responsiblePersonError && <div className="small-red-text">{responsiblePersonError}</div>}
          </div>

          <div className="input-container">
            <label className={'input-label-right'}>Email Frequency: </label>
            <label className="query-frequency-label">Every </label>
            <input
              className="text-input-xsmall"
              type="text"
              value={queryIntervalDays}
              disabled={fieldsDisabled}
              onChange={({ target }) => {
                setQueryIntervalDays(target.value);
              }}
            />
            <label className="query-frequency-label">hours, escalate after </label>
            <input
              className="text-input-xsmall"
              type="text"
              value={queryDurationDays}
              disabled={fieldsDisabled}
              onChange={({ target }) => {
                setQueryDurationDays(target.value);
              }}
            />
            <label className="query-frequency-label">days.</label>
            {toolTipsEnabled && (
              <div className="form-tooltips">
                <ToolTip
                  tooltipExternalReference={'capaNotificationSettings123'}
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName={'Email Frequency:'}
                />
              </div>
            )}
          </div>

          <div className="input-container-main">
            <div className="input-container">
              <label className="input-label-right">CC Users:</label>
              <Select
                options={allPersonList}
                closeMenuOnSelect={true}
                isMulti={true}
                onChange={handleCcUsersChange}
                className="input-select"
                classNamePrefix="input-select"
                controlShouldRenderValue={false}
                isDisabled={fieldsDisabled}
                value={selectedCcUsers}
              />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference={'queryCCUsers123'}
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName={'CC Users:'}
                />
              )}
            </div>

            {selectedCcUsers && selectedCcUsers.length > 0 && (
              <div
                className="query-cc-users-container"
                style={{ overflowY: selectedCcUsers?.length > 2 ? 'auto' : 'hidden' }}
              >
                {selectedCcUsers.map((user, index) => (
                  <div key={index} className="input-container-below">
                    <input
                      type="text"
                      value={`${user.label}`}
                      readOnly
                      disabled={fieldsDisabled}
                      className="text-input"
                    />
                    <button
                      className="delete-section-button"
                      type="button"
                      disabled={fieldsDisabled}
                      onClick={() => handleRemoveCcUser(index)}
                    >
                      X
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="input-container-main">
            <div className="input-container">
              <label className="input-label-right">Escalation Contact:</label>
              <Select
                options={allPersonList}
                closeMenuOnSelect={true}
                isMulti={true}
                onChange={handleEscalatedUsersChange}
                className="input-select"
                classNamePrefix="input-select"
                controlShouldRenderValue={false}
                isDisabled={fieldsDisabled}
                value={selectedEscalationUsers}
              />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference={'queryEscalationContact123'}
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName={'Escalation Contact:'}
                />
              )}
            </div>

            {selectedEscalationUsers && selectedEscalationUsers.length > 0 && (
              <div
                className="query-cc-users-container"
                style={{ overflowY: selectedEscalationUsers?.length > 2 ? 'auto' : 'hidden' }}
              >
                {selectedEscalationUsers.map((user, index) => (
                  <div key={index} className="input-container-below">
                    <input
                      type="text"
                      value={`${user.label}`}
                      readOnly
                      disabled={fieldsDisabled}
                      className="text-input"
                    />
                    <button
                      className="delete-section-button"
                      type="button"
                      disabled={fieldsDisabled}
                      onClick={() => handleRemoveEscalatedUser(index)}
                    >
                      X
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {editMode && !isNew && (
            <div>
              <div className="input-container">
                <label className={'input-label-right'}>Status:</label>
                <Select
                  options={STATUS_LIST.map((status) => ({
                    value: status,
                    label: status,
                  }))}
                  closeMenuOnSelect={true}
                  onChange={setStatus}
                  value={status || null}
                  className="input-select"
                  classNamePrefix={`input-select${statusError && 'error-border'}`}
                  isDisabled={statusActionFieldsDisabled}
                />
                {toolTipsEnabled && (
                  <ToolTip
                    tooltipExternalReference={'QueryStatus123'}
                    context={context}
                    setSpinnerText={setSpinnerText}
                    showToaster={showToaster}
                    fieldName={'Status:'}
                  />
                )}
              </div>
              <div className="error-message-right">
                {statusError && <div className="small-red-text">{statusError}</div>}
              </div>
            </div>
          )}

          {editMode && !isNew && (
            <div className="input-container">
              <label className={'input-area-label-right'}>Action Taken:</label>
              <textarea
                className="input-area-sm"
                spellCheck={true}
                type="text"
                value={actionTaken}
                disabled={statusActionFieldsDisabled}
                onChange={({ target }) => {
                  setActionTaken(target.value);
                }}
              />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference={'QueryActionTaken123'}
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName={'Action Taken:'}
                />
              )}
            </div>
          )}

          {editMode && !isNew && (
            <div>
              <div className="input-container">
                <label className={'input-label-right'}>Actioned By:</label>
                <Select
                  options={responsiblePersonList}
                  closeMenuOnSelect={true}
                  onChange={setActionedBy}
                  value={actionedBy || null}
                  className="input-select"
                  classNamePrefix={`input-select${actionByError && 'error-border'}`}
                  isDisabled={statusActionFieldsDisabled}
                />
                {toolTipsEnabled && (
                  <ToolTip
                    tooltipExternalReference={'QueryStatus123'}
                    context={context}
                    setSpinnerText={setSpinnerText}
                    showToaster={showToaster}
                    fieldName={'Actioned By:'}
                  />
                )}
              </div>
              <div className="error-message-right">
                {actionByError && <div className="small-red-text">{actionByError}</div>}
              </div>
            </div>
          )}

          {editMode && !isNew && (
            <div>
              <div className="input-container">
                <label className={'input-label-right'}>Action Date:</label>
                <DatePicker
                  selected={actionDate}
                  className={`text-input ${actionDateError && 'error-border'}`}
                  dateFormat="dd-MM-yyyy"
                  showPopperArrow={false}
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                  placeholderText="Select..."
                  disabled={statusActionFieldsDisabled}
                  onChange={(selectedDate) => setActionDate(selectedDate)}
                />
                <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon" />
                {toolTipsEnabled && (
                  <ToolTip
                    tooltipExternalReference={'QueryStatus123'}
                    context={context}
                    setSpinnerText={setSpinnerText}
                    showToaster={showToaster}
                    fieldName={'Action Date:'}
                  />
                )}
              </div>
              <div className="error-message">
                {actionDateError && <div className="small-red-text">{actionDateError}</div>}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={'review-button-container'} style={{ margin: '10px' }}>
        {editMode && (
          <>
            <SaveButton
              textColor={'#74a47f'}
              color={'#ffffff'}
              border={'1px solid #74a47f'}
              title={'Cancel'}
              onClick={handleCancelEditClick}
            />
            <SaveButton
              color={'#74a47f'}
              title={'Save and Close'}
              border={'1px solid #74a47f'}
              onClick={handleSaveAndCloseEditClick}
              disabled={
                fieldsDisabled &&
                responseFieldsDisabled &&
                queryResponseAndCommentsDisabled &&
                responsiblePersonDisabled
              }
            />
          </>
        )}
        {!editMode && (
          <>
            <SaveButton
              textColor={'#74a47f'}
              color={'#ffffff'}
              border={'1px solid #74a47f'}
              title={'Save and New'}
              onClick={handleSaveAndNewClick}
            />
            <SaveButton
              color={'#74a47f'}
              title={'Save and Close'}
              border={'1px solid #74a47f'}
              onClick={handleSaveAndCloseClick}
            />
          </>
        )}
      </div>
    </div>
  );
};
